'use client'
import { dayDiff, getFormattedDate, getMonthRange } from '@/src/utils/date/dateHelper'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { alpha, Box, useTheme } from '@mui/material'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import DayPicker, { DateUtils } from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import './_calendar.scss'

const Calendar = ({
    selectedDates = [],
    className = '',
    title = '',
    roundTrip,
    onClickCalendar,
    priceData,
    activeOnward,
    activeReturn,
    isRtl,
    isMulticity,
    multiCityDestinations,
    index,
    fromMonth,
    toMonth,
    numberOfMonths = 2,
    ...otherProps
}: any) => {
    const theme = useTheme()
    const tMonths = useTranslations('form.months')

    const NO_OF_MONTHS = numberOfMonths

    const today = fromMonth ? fromMonth : new Date()
    const maxToMonth = toMonth ? toMonth : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    const locale = 'AE'
    
    // Optimized: Use props directly with useMemo instead of state + useEffect
    const baseRange = useMemo(() => selectedDates, [selectedDates])
    const baseSelectedDate = useMemo(() => selectedDates['from'], [selectedDates])
    
    // Keep state only for hover interactions (enteredTo)
    const [hoveredTo, setHoveredTo] = useState<Date | null>(null)
    const [localSelectedDate, setLocalSelectedDate] = useState<Date | null>(baseSelectedDate)
    
    // Sync localSelectedDate when prop changes (only when actually different)
    useEffect(() => {
        setLocalSelectedDate((prev) => {
            if (baseSelectedDate) {
                const isDifferent = !prev || 
                    baseSelectedDate.getTime() !== prev.getTime()
                return isDifferent ? baseSelectedDate : prev
            }
            return null
        })
    }, [baseSelectedDate])
    
    // Merge base range with hover state
    const range = useMemo(() => ({
        ...baseRange,
        enteredTo: hoveredTo || baseRange.enteredTo
    }), [baseRange, hoveredTo])
    
    const selectedDate = localSelectedDate || baseSelectedDate
    
    const dayPickerClassName = classNames(className, {
        Selectable: roundTrip && !isRtl,
        'Selectable--rtl': isRtl && roundTrip
    })
    const { from, to, enteredTo } = range
    
    // Optimized: Memoize modifiers to prevent DayPicker re-renders
    const modifiers = useMemo(() => ({
        start: from,
        end: enteredTo,
        leftEdge: { daysOfWeek: [1] },
        rightEdge: { daysOfWeek: [0] }
    }), [from, enteredTo])
    
    const { fareCalendar = {}, cheapestPricesByMonth = {}, directionClick, minDate, formatCurrency } = otherProps
    
    // Optimized: Memoize disabledDays to prevent DayPicker re-renders
    const disabledDays = useMemo(() => {
        const beforeDate = minDate
            ? minDate
            : isMulticity && index > 1 && !isEmpty(multiCityDestinations[`depart_date_${index - 1}`])
              ? new Date(getFormattedDate(multiCityDestinations[`depart_date_${index - 1}`], 'MM/DD/YYYY'))
              : activeReturn && range.from
                ? range.from
                : today
        
        return {
            before: beforeDate,
            after: maxToMonth
        }
    }, [minDate, isMulticity, index, multiCityDestinations, activeReturn, range.from, today, maxToMonth])

    function isSelectingFirstDay(from: any, to: any, day: any) {
        if (roundTrip && activeReturn) {
            const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from)
            const isRangeSelected = from && to
            return !isBeforeFirstDay || isRangeSelected
        }
        if (isMulticity) {
            // && index > 1 && !isEmpty(multiCityDestinations[`depart_date_${index - 1}`])) {
            const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from)
            const isRangeSelected = from && to
            return !isBeforeFirstDay || isRangeSelected
        }
        return false
    }

    function handleDayMouseEnter(day: any) {
        const { from, to } = range
        if (isSelectingFirstDay(from, to, day)) {
            setHoveredTo(day)
        }
    }
    function handleDayClick(day: any) {
        if (roundTrip && activeOnward) {
            if (from && to) {
                let diff = dayDiff(day, to)
                if (diff >= 1) {
                    setLocalSelectedDate(day)
                    onClickCalendar({
                        from: day,
                        to: day,
                        enteredTo: day
                    })
                } else {
                    setLocalSelectedDate(day)
                    onClickCalendar({
                        ...range,
                        from: day
                    })
                }
            } else if (from && isEmpty(to)) {
                setLocalSelectedDate(day)
                onClickCalendar({
                    ...range,
                    from: day
                })
            } else {
                // No start date yet — mirrors mobileCalendar, which already handles this.
                // Flights always seeds range.from, so only date-optional forms reach here.
                setLocalSelectedDate(day)
                onClickCalendar({
                    from: day,
                    to: null,
                    enteredTo: null
                })
            }
            // Clevertap.event('Air_UI_Action', {
            //     action_type: 'BUTTON',
            //     action_name: 'SRP_OW_Date_Selected'
            // })
        } else if (activeReturn) {
            // Clevertap.event('Air_UI_Action', {
            //     action_type: 'BUTTON',
            //     action_name: 'SRP_RT_Date_Selected'
            // })
            setLocalSelectedDate(day)
            onClickCalendar({
                ...range,
                to: day,
                enteredTo: day
            })
        } else {
            // Clevertap.event('Air_UI_Action', {
            //     action_type: 'BUTTON',
            //     action_name: 'SRP_OW_Date_Selected'
            // })
            setLocalSelectedDate(day)
            onClickCalendar({
                from: day,
                to: null,
                enteredTo: null
            })
        }
    }
    function nextClick(onNextClick: any, previousMonth: any) {
        let monthRange = getMonthRange(previousMonth, 'right')
        directionClick(monthRange)
        onNextClick()
    }
    function previousClick(onPreviousClick: any, previousMonth: any) {
        let monthRange = getMonthRange(previousMonth, 'left')
        directionClick(monthRange)
        onPreviousClick()
    }
    function Navbar({ onPreviousClick, onNextClick, previousMonth, nextMonth, maxToMonth: navMaxToMonth }: any) {
        const isDisabled =
            (previousMonth.getFullYear() === today.getFullYear() &&
                previousMonth.getMonth() < today.getMonth()) ||
            (previousMonth.getFullYear() < today.getFullYear() &&
                previousMonth.getMonth() > today.getMonth())
        
        return (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 20px',
                        position: 'absolute',
                        width: '100%'
                    }}
                >
                    <Box>
                        {isDisabled ? (
                            isRtl ? (
                                <ArrowForward color='disabled' height={24} width={24} style={{cursor: 'pointer'}}/>
                            ) : (
                                <ArrowBack color='disabled' height={24} width={24} style={{cursor: 'pointer'}} />
                            )
                        ) : isRtl ? (
                            <ArrowForward
                                data-testid="leftArrow"
                                height={24}
                                width={24}
                                style={{cursor: 'pointer'}}
                                onClick={() => previousClick(onPreviousClick, previousMonth)}
                            />
                        ) : (
                            <ArrowBack
                                data-testid="leftArrow"
                                height={24}
                                width={24}
                                style={{cursor: 'pointer'}}
                                onClick={() => previousClick(onPreviousClick, previousMonth)}
                            />
                        )}
                    </Box>

                    <Box>
                        {nextMonth.getTime() > navMaxToMonth.getTime() ? (
                            isRtl ? (
                                <ArrowBack
                                    height={24}
                                    width={24}
                                    style={{ alignSelf: 'flex-end', cursor: 'pointer'}}
                                />
                            ) : (
                                <ArrowForward
                                    height={24}
                                    width={24}
                                    style={{ alignSelf: 'flex-end', cursor: 'pointer'}}
                                />
                            )
                        ) : isRtl ? (
                            <ArrowBack
                                data-testid="rightArrow"
                                height={24}
                                width={24}
                                style={{ alignSelf: 'flex-end', cursor: 'pointer'}}
                                onClick={() => nextClick(onNextClick, previousMonth)}
                            />
                        ) : (
                            <ArrowForward
                                data-testid="rightArrow"
                                height={24}
                                width={24}
                                style={{ alignSelf: 'flex-end', cursor: 'pointer'}}
                                onClick={() => nextClick(onNextClick, previousMonth)}
                            />
                        )}
                    </Box>
                </Box>
            </>
        )
    }

    const renderDay = (day: Date) => {
        const date = day.getDate()
        
        // --- Fare data lookup ---
        const dateKey = dayjs(day).format('YYYY-MM-DD')
        const showFareCalendar = !isEmpty(fareCalendar) 
        const fare = fareCalendar?.[dateKey]
        const hasFare = fare && fare.amount !== undefined && fare.amount !== null
        const price = fare?.amount
        const monthKey = dayjs(day).format('YYYY-MM')
        const cheapestPrice = cheapestPricesByMonth?.[monthKey]
        const isCheapest = hasFare && cheapestPrice && price === cheapestPrice
        
        // --- Selection logic ---
        const isStart = roundTrip && from && DateUtils.isSameDay(day, from)
        const isEnd = roundTrip && to && DateUtils.isSameDay(day, to)
        const isSelected = (!roundTrip && selectedDate && DateUtils.isSameDay(day, selectedDate)) || isStart || isEnd
        const isInRange = roundTrip && from && to && DateUtils.isDayAfter(day, from) && DateUtils.isDayBefore(day, to)
        const isHoveredRange =
            roundTrip && from && enteredTo && DateUtils.isDayAfter(day, from) && DateUtils.isDayBefore(day, enteredTo)
        
        // --- Disabled date logic ---
        const isBeforeMinDate = Boolean(minDate && DateUtils.isDayBefore(day, minDate))
        const isDisabled =
            DateUtils.isDayBefore(day, today) ||
            isBeforeMinDate ||
            (isMulticity &&
                index > 1 &&
                !isEmpty(multiCityDestinations[`depart_date_${index - 1}`]) &&
                DateUtils.isDayBefore(
                    day,
                    new Date(getFormattedDate(multiCityDestinations[`depart_date_${index - 1}`], 'MM/DD/YYYY'))
                )) ||
            (activeReturn && range.from && DateUtils.isDayBefore(day, range.from))

        // Hotels' check-out leg passes minDate = check-in (+1 day), which disables the
        // already-picked check-in. Keep a picked endpoint highlighted when minDate is the
        // only thing blocking it; it stays non-clickable via `cursor` below.
        const keepSelectedHighlight = Boolean(
            (isStart || isEnd) && isBeforeMinDate && !DateUtils.isDayBefore(day, today)
        )

        // --- Style logic ---
        let bgColor = 'transparent'
        let color = '#1a1a1a'
        const cursor = isDisabled ? 'not-allowed' : 'pointer'
        let boxShadow = 'none' // No elevation/shadow

        if (isDisabled && !keepSelectedHighlight) {
            color = '#9e9e9e' // grey text for disabled dates
        } else if (isSelected) {
            bgColor = theme.palette.primary.main
            color = '#fff'
            boxShadow = 'none' // No elevation for selected date
        } else if (isInRange || isHoveredRange) {
            bgColor = alpha(theme.palette.primary.main, 0.08)  
            color = '#1a1a1a'
        }

        return (
            <Box
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                sx={{
                    width: (roundTrip || !showFareCalendar) ? 40 : 48,
                    height: (roundTrip || !showFareCalendar) ? 35 : 40, // Reduced height when no prices shown (round trip)
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: (roundTrip || !showFareCalendar) ? 'center' : 'flex-start', // Center date when no price
                    padding: 0.5, // p-1 equivalent: 4px vertical padding
                    margin: 0.5, // Add margin for spacing: 4px vertical, 10px horizontal
                    backgroundColor: bgColor,
                    borderRadius: 0.5,
                    cursor,
                    boxShadow: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    position: 'relative',
                    // Hover works even in margin area, and all elements change together
                    '&:hover': !isDisabled
                        ? {
                              backgroundColor: theme.palette.primary.main,
                              boxShadow: 'none',
                              '& .date-text': {
                                  color: '#fff !important'
                              },
                              '& .price-text': {
                                  color: '#fff !important'
                              }
                          }
                        : {}
                }}
            >
                <Box
                    className="date-text"
                    sx={{
                        fontSize: 14, // fs-14
                        fontWeight: 500, // fw-500
                        color,
                        lineHeight: 1.2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '0 0 auto',
                        pointerEvents: 'none' // Allow hover to work on parent
                    }}
                >
                    {date}
                </Box>
                {!isDisabled && !roundTrip && showFareCalendar && (
                    <Box
                        className="price-text"
                        sx={{
                            fontSize: 10, // fs-10
                            lineHeight: '12px', // lh-12: fixed 12px line height
                            marginTop: '2px',
                            color: isSelected
                                ? '#fff'
                                : isCheapest
                                    ? theme.palette.success.main // c-success-500
                                    : theme.palette.text.secondary, // c-neutral-400
                            fontWeight: isCheapest ? 700 : 600, // Bolder font weight for prices
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: '0 0 auto',
                            minHeight: '12px',
                            opacity: 1, // Ensure price is always visible
                            visibility: 'visible', // Ensure price is always visible
                            pointerEvents: 'none' // Allow hover to work on parent
                        }}
                    >
                        {hasFare
                            ? formatCurrency ? formatCurrency(price, true, 0) : price.toLocaleString('en-US', {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0
                              })
                            : '-'}
                    </Box>
                )}
            </Box>
        )
    }
    const MONTHS = [
        tMonths('january'),
        tMonths('february'),
        tMonths('march'),
        tMonths('april'),
        tMonths('may'),
        tMonths('june'),
        tMonths('july'),
        tMonths('august'),
        tMonths('september'),
        tMonths('october'),
        tMonths('november'),
        tMonths('december')
    ]

    const weekSort = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
    const weekSortAr = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
    const FIRST_DAY_OF_WEEK: { [key: string]: number } = {
        IN: 1,
        AE: 0,
        SA: 0,
        KW: 0,
        BH: 0,
        QA: 0,
        OM: 0,
        ME: 0
    }
    
    return (
        <Box
            sx={{
                padding: { xs: 1, md: 2 },
                overflow: 'hidden', // Prevent scrollbar
                '& .DayPicker-wrapper': {
                    paddingBottom: 0, // Remove default bottom padding
                    overflow: 'hidden' // Prevent scrollbar
                },
                '& .DayPicker-Months': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 3, // 24px gap between months using theme spacing
                    margin: 0,
                    alignItems: 'flex-start' // Align months at the top for row-by-row alignment
                },
                '& .DayPicker-Month': {
                    margin: 0,
                    display: 'table',
                    width: 'auto',
                    '& table': {
                        width: '100%',
                        tableLayout: 'fixed', // Fixed layout for consistent column widths
                        borderCollapse: 'separate',
                        borderSpacing: 0, // Remove borderSpacing, using margin on cells instead
                        margin: 0,
                        marginBottom: 0 // Remove bottom margin
                    },
                    '& thead': {
                        display: 'table-header-group'
                    },
                    '& tbody': {
                        display: 'table-row-group'
                    }
                },
                '& .DayPicker-Caption': {
                    marginBottom: 1.5, // 12px spacing using theme
                    textAlign: 'center',
                    '& > div': {
                        fontSize: { xs: '0.875rem', md: '0.875rem' },
                        fontWeight: 500,
                        color: 'text.primary'
                    }
                },
                '& .DayPicker-Weekdays': {
                    display: 'table-header-group',
                    '& tr': {
                        display: 'table-row'
                    }
                },
                '& .DayPicker-Weekday': {
                    padding: 1.5, // 8px vertical padding using theme
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    width: '48px', // Match date cell width
                    display: 'table-cell',
                    verticalAlign: 'middle'
                },
                '& .DayPicker-Body': {
                    display: 'table-row-group'
                },
                        '& .DayPicker-Week': {
                            display: 'table-row',
                            height: 'auto' // Allow natural height for rows
                        },
                        '& .DayPicker-Day': {
                            width: '48px',
                            height: 'auto',
                            padding: 0,
                            margin: 0,
                            display: 'table-cell',
                            verticalAlign: 'top', // Align to top like flex-top in HTML
                            textAlign: 'center',
                            position: 'relative'
                        }
            }}
        >
            <DayPicker
                {...otherProps}
                numberOfMonths={NO_OF_MONTHS}
                interactionDisabled={true}
                renderDay={renderDay}
                className={dayPickerClassName}
                weekdaysShort={isRtl ? weekSortAr : weekSort}
                firstDayOfWeek={FIRST_DAY_OF_WEEK[locale]}
                disabledDays={disabledDays}
                fromMonth={today}
                toMonth={maxToMonth}
                modifiers={modifiers}
                locale={isRtl ? 'ar' : 'en'}
                months={MONTHS}
                month={
                    isMulticity && index > 1 && !isEmpty(multiCityDestinations[`depart_date_${index - 1}`])
                        ? new Date(getFormattedDate(multiCityDestinations[`depart_date_${index - 1}`], 'MM/DD/YYYY'))
                        : range['from']
                } // This is to be change wrt return / onward selection
                selectedDays={roundTrip ? [from, { from, to: enteredTo }] : selectedDate}
                navbarElement={<Navbar maxToMonth={maxToMonth} />}
                onDayClick={day => handleDayClick(day)}
                onDayMouseEnter={day => handleDayMouseEnter(day)}
            />
        </Box>
    )
}

export default Calendar
